import React, { useEffect, useState } from 'react';
import { Snackbar, Box,
  Card,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
  Chip,
  InputAdornment,
  Tabs,
  Tab,
  TablePagination, } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Visibility,
  VisibilityOff,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';

import { Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { User, usersAPI, jobGroupsAPI, JobGroup, organizationalUnitsAPI, OrganizationalUnit, companiesAPI, Company } from '../../services/api';
import ExcelUpload from '../../components/ExcelUpload';
import { toPersianNumber } from '../../utils/persianNumber';

// Helper function to convert gender codes to Persian
const getGenderLabel = (gender: string | undefined | null): string => {
  if (!gender) return '-';
  const normalized = gender.toLowerCase().trim();
  if (normalized === 'm' || normalized === 'male') return 'آقا';
  if (normalized === 'f' || normalized === 'female') return 'خانم';
  return gender;
};

const AdminUsers: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [activeTab, setActiveTab] = useState<'active' | 'inactive'>('active');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [jobGroups, setJobGroups] = useState<JobGroup[]>([]);
  const [organizationalUnits, setOrganizationalUnits] = useState<OrganizationalUnit[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterGender, setFilterGender] = useState<string>('');
  const [filterCompany, setFilterCompany] = useState<string>('');
  const [filterWorkShift, setFilterWorkShift] = useState<string>('');
  const [sortField, setSortField] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [formData, setFormData] = useState<{
    firstName: string;
    lastName: string;
    mobile: string;
    personnelCode: string;
    gender: string;
    company: string;
    companyId: string;
    cardNumber: string;
    workShift: 'day' | 'night';
    password: string;
    jobGroupId: string;
    organizationalUnitId: string;
  }>({
    firstName: '',
    lastName: '',
    mobile: '',
    personnelCode: '',
    gender: '',
    company: '',
    companyId: '',
    cardNumber: '',
    workShift: 'day',
    password: '',
    jobGroupId: '',
    organizationalUnitId: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadUsers();
    loadJobGroups();
    loadOrganizationalUnits();
    loadCompanies();
  }, []);

  useEffect(() => {
    // 1. Tab filter (active vs inactive)
    let filtered = users.filter((user) => (activeTab === 'active' ? user.isActive : !user.isActive));

    // 2. Search filter
    if (searchTerm.trim() !== '') {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (user) =>
          user.firstName?.toLowerCase().includes(term) ||
          user.lastName?.toLowerCase().includes(term) ||
          user.mobile?.includes(term) ||
          user.personnelCode?.toLowerCase().includes(term) ||
          user.company?.toLowerCase().includes(term) ||
          user.cardNumber?.toLowerCase().includes(term)
      );
    }

    // 3. Gender filter
    if (filterGender) {
      filtered = filtered.filter((user) => {
        const userGender = user.gender?.toLowerCase().trim();
        if (filterGender === 'male') {
          return userGender === 'm' || userGender === 'male';
        }
        if (filterGender === 'female') {
          return userGender === 'f' || userGender === 'female';
        }
        return user.gender === filterGender;
      });
    }

    // 4. Company filter
    if (filterCompany) {
      filtered = filtered.filter((user) => user.companyId === filterCompany || user.company === filterCompany);
    }

    // 5. Work shift filter
    if (filterWorkShift) {
      filtered = filtered.filter((user) => user.workShift === filterWorkShift);
    }

    // 6. Sort
    if (sortField) {
      filtered = [...filtered].sort((a, b) => {
        let aValue: any;
        let bValue: any;

        switch (sortField) {
          case 'personnelCode':
            aValue = a.personnelCode || '';
            bValue = b.personnelCode || '';
            break;
          case 'firstName':
            aValue = a.firstName || '';
            bValue = b.firstName || '';
            break;
          case 'jobGroupTitle':
            aValue = a.jobGroupTitle || '';
            bValue = b.jobGroupTitle || '';
            break;
          case 'organizationalUnitTitle':
            aValue = a.organizationalUnitTitle || '';
            bValue = b.organizationalUnitTitle || '';
            break;
          case 'lastName':
            aValue = a.lastName || '';
            bValue = b.lastName || '';
            break;
          case 'mobile':
            aValue = a.mobile || '';
            bValue = b.mobile || '';
            break;
          case 'gender':
            aValue = getGenderLabel(a.gender);
            bValue = getGenderLabel(b.gender);
            break;
          case 'company':
            aValue = a.company || '';
            bValue = b.company || '';
            break;
          case 'cardNumber':
            aValue = a.cardNumber || '';
            bValue = b.cardNumber || '';
            break;
          case 'workShift':
            aValue = a.workShift === 'day' ? 'روز' : a.workShift === 'night' ? 'شب' : '';
            bValue = b.workShift === 'day' ? 'روز' : b.workShift === 'night' ? 'شب' : '';
            break;
          case 'isActive':
            aValue = a.isActive ? 'فعال' : 'غیرفعال';
            bValue = b.isActive ? 'فعال' : 'غیرفعال';
            break;
          default:
            return 0;
        }

        if (typeof aValue === 'string' && typeof bValue === 'string') {
          const comparison = aValue.localeCompare(bValue, 'fa');
          return sortOrder === 'asc' ? comparison : -comparison;
        }

        if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      });
    }

    setFilteredUsers(filtered);
    setPage(0);
  }, [searchTerm, filterGender, filterCompany, filterWorkShift, sortField, sortOrder, activeTab, users]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await usersAPI.getUsers('staff');
      setUsers(data);
      setFilteredUsers(data);
    } catch (err: any) {
      setError('خطا در بارگذاری کاربران');
    } finally {
      setLoading(false);
    }
  };

  const loadJobGroups = async () => {
    try {
      const data = await jobGroupsAPI.getJobGroups();
      setJobGroups(data);
    } catch (err) {
      console.error('Error loading job groups:', err);
    }
  };

  const loadOrganizationalUnits = async () => {
    try {
      const data = await organizationalUnitsAPI.getOrganizationalUnits();
      setOrganizationalUnits(data);
    } catch (err) {
      console.error('Error loading organizational units:', err);
    }
  };

  const loadCompanies = async () => {
    try {
      const data = await companiesAPI.getCompanies();
      setCompanies(data);
    } catch (err) {
      console.error('Error loading companies:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      await usersAPI.createUser({
        ...formData,
        workShift: formData.workShift as 'day' | 'night',
        role: 'staff',
      });
      setSuccess('کاربر با موفقیت اضافه شد');
      setOpenModal(false);
      setFormData({
        firstName: '',
        lastName: '',
        mobile: '',
        personnelCode: '',
        gender: '',
        company: '',
        companyId: '',
        cardNumber: '',
        workShift: 'day',
        password: '',
        jobGroupId: '',
        organizationalUnitId: '',
      });
      loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'خطا در اضافه کردن کاربر');
    }
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setFormData({
      firstName: user.firstName,
      lastName: user.lastName,
      mobile: user.mobile.startsWith('0') ? user.mobile : '0' + user.mobile,
      personnelCode: user.personnelCode || '',
      gender: user.gender || '',
      company: user.company || '',
      companyId: user.companyId || '',
      cardNumber: user.cardNumber || '',
      workShift: (user.workShift || 'day') as 'day' | 'night',
      password: '',
      jobGroupId: user.jobGroupId || '',
      organizationalUnitId: user.organizationalUnitId || '',
    });
    setOpenEditModal(true);
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!editingUser) return;

    try {
      await usersAPI.updateUser(editingUser.id, {
        ...formData,
        workShift: formData.workShift as 'day' | 'night',
      });
      setSuccess('کاربر با موفقیت به‌روزرسانی شد');
      setOpenEditModal(false);
      setEditingUser(null);
      setFormData({
        firstName: '',
        lastName: '',
        mobile: '',
        personnelCode: '',
        gender: '',
        company: '',
        companyId: '',
        cardNumber: '',
        workShift: 'day',
        password: '',
        jobGroupId: '',
        organizationalUnitId: '',
      });
      loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'خطا در به‌روزرسانی کاربر');
    }
  };

  const handleToggleActive = async (userId: string) => {
    try {
      // Optimistic update
      setUsers((prev) =>
        prev.map((u) => (u.id === userId ? { ...u, isActive: !u.isActive } : u))
      );
      await usersAPI.toggleUserActive(userId);
      setSuccess('وضعیت کاربر با موفقیت تغییر کرد');
    } catch (err: any) {
      setError('خطا در تغییر وضعیت کاربر');
      loadUsers();
    }
  };

  const handleDelete = async (userId: string) => {
    if (!window.confirm('آیا از حذف این کاربر مطمئن هستید؟')) return;

    try {
      await usersAPI.deleteUser(userId);
      setSuccess('کاربر با موفقیت حذف شد');
      loadUsers();
    } catch (err: any) {
      setError('خطا در حذف کاربر');
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (field: string) => {
    if (sortField !== field) return null;
    return sortOrder === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />;
  };

  const activeCount = users.filter((u) => u.isActive).length;
  const inactiveCount = users.filter((u) => !u.isActive).length;
  const paginatedUsers = filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Page Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontFamily: 'Vazir, sans-serif',
            fontWeight: 600,
          }}
        >
          مدیریت پرسنل
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <ExcelUpload onUploadSuccess={loadUsers} />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenModal(true)}
            sx={{ fontFamily: 'Vazir, sans-serif', height: 48, borderRadius: '12px' }}
          >
            افزودن پرسنل
          </Button>
        </Box>
      </Box>

      <Snackbar open={!!error} autoHideDuration={2000} onClose={() => { if(typeof setError === 'function') setError(''); }} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
  <Alert severity="error" sx={{ width: '100%', fontFamily: 'Vazir, sans-serif' }} onClose={() => { if(typeof setError === 'function') setError(''); }}>
    {error}
  </Alert>
</Snackbar>
      <Snackbar open={!!success} autoHideDuration={2000} onClose={() => setSuccess('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
  <Alert severity="success" sx={{ width: '100%', fontFamily: 'Vazir, sans-serif' }} onClose={() => setSuccess('')}>
    {success}
  </Alert>
</Snackbar>

      {/* Filter */}
      <Card sx={{ mb: 3 }}>
        <Box sx={{ p: 2 }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' }, 
            gap: 2, 
            mb: 2, 
            alignItems: { xs: 'stretch', md: 'center' },
            '& .MuiInputBase-root': { height: 48, borderRadius: '12px' },
            '& .MuiOutlinedInput-input, & .MuiSelect-select': { padding: '12.5px 14px' },
            '& .MuiInputLabel-root:not(.MuiInputLabel-shrink)': { top: -4 }
          }}>
            <TextField
              placeholder="جستجو..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{
                fontFamily: 'Vazir, sans-serif',
                flex: { xs: '1', md: '2' },
                minWidth: { xs: '100%', md: '200px' },
              }}
              InputLabelProps={{
                sx: { fontFamily: 'Vazir, sans-serif' },
              }}
              inputProps={{
                sx: { fontFamily: 'Vazir, sans-serif' },
              }}
            />
            <FormControl sx={{ flex: { xs: '1', md: '1' }, minWidth: { xs: '100%', md: '150px' } }}>
              <InputLabel sx={{ fontFamily: 'Vazir, sans-serif' }}>جنسیت</InputLabel>
              <Select
                value={filterGender}
                onChange={(e) => setFilterGender(e.target.value)}
                label="جنسیت"
                sx={{ fontFamily: 'Vazir, sans-serif' }}
              >
                <MenuItem value="" sx={{ fontFamily: 'Vazir, sans-serif' }}>همه</MenuItem>
                <MenuItem value="male" sx={{ fontFamily: 'Vazir, sans-serif' }}>مرد</MenuItem>
                <MenuItem value="female" sx={{ fontFamily: 'Vazir, sans-serif' }}>زن</MenuItem>
              </Select>
            </FormControl>
            <FormControl sx={{ flex: { xs: '1', md: '1' }, minWidth: { xs: '100%', md: '150px' } }}>
              <InputLabel sx={{ fontFamily: 'Vazir, sans-serif' }}>شرکت</InputLabel>
              <Select
                value={filterCompany}
                onChange={(e) => setFilterCompany(e.target.value)}
                label="شرکت"
                sx={{ fontFamily: 'Vazir, sans-serif' }}
              >
                <MenuItem value="" sx={{ fontFamily: 'Vazir, sans-serif' }}>همه</MenuItem>
                {companies.map((company) => (
                  <MenuItem key={company.id} value={company.id} sx={{ fontFamily: 'Vazir, sans-serif' }}>
                    {company.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ flex: { xs: '1', md: '1' }, minWidth: { xs: '100%', md: '150px' } }}>
              <InputLabel sx={{ fontFamily: 'Vazir, sans-serif' }}>شیفت</InputLabel>
              <Select
                value={filterWorkShift}
                onChange={(e) => setFilterWorkShift(e.target.value)}
                label="شیفت"
                sx={{ fontFamily: 'Vazir, sans-serif' }}
              >
                <MenuItem value="" sx={{ fontFamily: 'Vazir, sans-serif' }}>همه</MenuItem>
                <MenuItem value="day" sx={{ fontFamily: 'Vazir, sans-serif' }}>روز</MenuItem>
                <MenuItem value="night" sx={{ fontFamily: 'Vazir, sans-serif' }}>شب</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Card>

      {/* Tabs for Active vs Inactive Personnel */}
      <Card sx={{ mb: 3, borderRadius: '16px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
        <Tabs
          value={activeTab}
          onChange={(_, val) => {
            setActiveTab(val);
            setPage(0);
          }}
          indicatorColor="primary"
          textColor="primary"
          sx={{
            px: 2,
            minHeight: '52px',
            '& .MuiTab-root': {
              fontFamily: 'Vazir, sans-serif',
              fontSize: '15px',
              fontWeight: 600,
              minHeight: '52px',
              textTransform: 'none',
              px: 3,
            },
          }}
        >
          <Tab
            value="active"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>پرسنل فعال</span>
                <Chip
                  label={toPersianNumber(activeCount.toString())}
                  size="small"
                  color={activeTab === 'active' ? 'primary' : 'default'}
                  sx={{
                    fontFamily: 'Vazir, sans-serif',
                    height: 24,
                    fontSize: '13px',
                    fontWeight: 600,
                    borderRadius: '8px',
                  }}
                />
              </Box>
            }
          />
          <Tab
            value="inactive"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>پرسنل غیرفعال</span>
                <Chip
                  label={toPersianNumber(inactiveCount.toString())}
                  size="small"
                  color={activeTab === 'inactive' ? 'error' : 'default'}
                  sx={{
                    fontFamily: 'Vazir, sans-serif',
                    height: 24,
                    fontSize: '13px',
                    fontWeight: 600,
                    borderRadius: '8px',
                  }}
                />
              </Box>
            }
          />
        </Tabs>
      </Card>

      {/* Table Card */}
      <Card sx={{ borderRadius: '16px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
        <TableContainer sx={{ bgcolor: '#fff', '& .MuiTableCell-root': { color: '#000', borderColor: 'rgba(0,0,0,0.1)' } }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600, width: 60 }}>ردیف</TableCell>
                <TableCell
                  sx={{
                    fontFamily: 'Vazir, sans-serif',
                    fontWeight: 600,
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': { backgroundColor: 'rgba(91, 124, 250, 0.1)' },
                  }}
                  onClick={() => handleSort('personnelCode')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    کد پرسنلی
                    {getSortIcon('personnelCode')}
                  </Box>
                </TableCell>
                <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600 }}>
                  گروه شغلی
                </TableCell>
                <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600 }}>
                  واحد سازمانی
                </TableCell>
                <TableCell
                  sx={{
                    fontFamily: 'Vazir, sans-serif',
                    fontWeight: 600,
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': { backgroundColor: 'rgba(91, 124, 250, 0.1)' },
                  }}
                  onClick={() => handleSort('firstName')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    نام
                    {getSortIcon('firstName')}
                  </Box>
                </TableCell>
                <TableCell
                  sx={{
                    fontFamily: 'Vazir, sans-serif',
                    fontWeight: 600,
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': { backgroundColor: 'rgba(91, 124, 250, 0.1)' },
                  }}
                  onClick={() => handleSort('lastName')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    نام خانوادگی
                    {getSortIcon('lastName')}
                  </Box>
                </TableCell>
                <TableCell
                  sx={{
                    fontFamily: 'Vazir, sans-serif',
                    fontWeight: 600,
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': { backgroundColor: 'rgba(91, 124, 250, 0.1)' },
                  }}
                  onClick={() => handleSort('mobile')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    موبایل
                    {getSortIcon('mobile')}
                  </Box>
                </TableCell>
                <TableCell
                  sx={{
                    fontFamily: 'Vazir, sans-serif',
                    fontWeight: 600,
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': { backgroundColor: 'rgba(91, 124, 250, 0.1)' },
                  }}
                  onClick={() => handleSort('gender')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    جنسیت
                    {getSortIcon('gender')}
                  </Box>
                </TableCell>
                <TableCell
                  sx={{
                    fontFamily: 'Vazir, sans-serif',
                    fontWeight: 600,
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': { backgroundColor: 'rgba(91, 124, 250, 0.1)' },
                  }}
                  onClick={() => handleSort('company')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    شرکت
                    {getSortIcon('company')}
                  </Box>
                </TableCell>
                <TableCell
                  sx={{
                    fontFamily: 'Vazir, sans-serif',
                    fontWeight: 600,
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': { backgroundColor: 'rgba(91, 124, 250, 0.1)' },
                  }}
                  onClick={() => handleSort('cardNumber')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    شماره کارت
                    {getSortIcon('cardNumber')}
                  </Box>
                </TableCell>
                <TableCell
                  sx={{
                    fontFamily: 'Vazir, sans-serif',
                    fontWeight: 600,
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': { backgroundColor: 'rgba(91, 124, 250, 0.1)' },
                  }}
                  onClick={() => handleSort('workShift')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    شیفت کاری
                    {getSortIcon('workShift')}
                  </Box>
                </TableCell>
                <TableCell
                  sx={{
                    fontFamily: 'Vazir, sans-serif',
                    fontWeight: 600,
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': { backgroundColor: 'rgba(91, 124, 250, 0.1)' },
                  }}
                  onClick={() => handleSort('isActive')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    وضعیت
                    {getSortIcon('isActive')}
                  </Box>
                </TableCell>
                <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600 }}>عملیات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={13} align="center" sx={{ py: 4 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontFamily: 'Vazir, sans-serif' }}
                    >
                      {activeTab === 'active'
                        ? (searchTerm || filterGender || filterCompany || filterWorkShift ? 'نتیجه‌ای در میان پرسنل فعال یافت نشد' : 'هیچ پرسنل فعالی یافت نشد')
                        : (searchTerm || filterGender || filterCompany || filterWorkShift ? 'نتیجه‌ای در میان پرسنل غیرفعال یافت نشد' : 'هیچ پرسنل غیرفعالی یافت نشد')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedUsers.map((user, index) => (
                  <TableRow key={user.id} hover>
                    <TableCell sx={{ fontFamily: 'Vazir, sans-serif' }}>{toPersianNumber((page * rowsPerPage + index + 1).toString())}</TableCell>
                    <TableCell sx={{ fontFamily: 'Vazir, sans-serif' }}>{user.personnelCode ? toPersianNumber(user.personnelCode) : '-'}</TableCell>
                    <TableCell sx={{ fontFamily: 'Vazir, sans-serif' }}>{user.jobGroupTitle || '-'}</TableCell>
                    <TableCell sx={{ fontFamily: 'Vazir, sans-serif' }}>{user.organizationalUnitTitle || '-'}</TableCell>
                    <TableCell 
                      sx={{ 
                        fontFamily: 'Vazir, sans-serif',
                        color: 'primary.main',
                        cursor: 'pointer',
                        fontWeight: 600,
                        '&:hover': { textDecoration: 'underline' }
                      }}
                      onClick={() => navigate(`/admin/users/${user.id}/reports`)}
                      title="مشاهده گزارش و رزروهای این پرسنل"
                    >
                      {user.firstName}
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        fontFamily: 'Vazir, sans-serif',
                        color: 'primary.main',
                        cursor: 'pointer',
                        fontWeight: 600,
                        '&:hover': { textDecoration: 'underline' }
                      }}
                      onClick={() => navigate(`/admin/users/${user.id}/reports`)}
                      title="مشاهده گزارش و رزروهای این پرسنل"
                    >
                      {user.lastName}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'Vazir, sans-serif' }}>{toPersianNumber(user.mobile.startsWith('0') ? user.mobile : '0' + user.mobile)}</TableCell>
                    <TableCell sx={{ fontFamily: 'Vazir, sans-serif' }}>{getGenderLabel(user.gender)}</TableCell>
                    <TableCell sx={{ fontFamily: 'Vazir, sans-serif' }}>{user.companyTitle || user.company || '-'}</TableCell>
                    <TableCell sx={{ fontFamily: 'Vazir, sans-serif' }}>{user.cardNumber ? toPersianNumber(user.cardNumber) : '-'}</TableCell>
                    <TableCell sx={{ fontFamily: 'Vazir, sans-serif' }}>
                      {user.workShift === 'day' ? 'روز' : user.workShift === 'night' ? 'شب' : '-'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.isActive ? 'فعال' : 'غیرفعال'}
                        color={user.isActive ? 'success' : 'error'}
                        size="small"
                        onClick={() => handleToggleActive(user.id)}
                        sx={{
                          fontFamily: 'Vazir, sans-serif',
                          cursor: 'pointer',
                          fontWeight: 500,
                          '&:hover': {
                            opacity: 0.8,
                          },
                        }}
                        title={user.isActive ? 'کلیک برای غیرفعال کردن' : 'کلیک برای فعال کردن'}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'nowrap' }}>
                        <IconButton
                          color="info"
                          size="small"
                          onClick={() => navigate(`/admin/users/${user.id}/reports`)}
                          sx={{ mr: 0.5 }}
                          title="مشاهده گزارش و رزروها"
                        >
                          <AssessmentIcon />
                        </IconButton>
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={() => handleEdit(user)}
                          sx={{ mr: 0.5 }}
                          title="ویرایش"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => handleDelete(user.id)}
                          sx={{ ml: 0.5 }}
                          title="حذف"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredUsers.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="تعداد در هر صفحه:"
          labelDisplayedRows={({ from, to, count }) =>
            `${toPersianNumber(from.toString())} تا ${toPersianNumber(to.toString())} از ${toPersianNumber(count.toString())}`
          }
          sx={{
            fontFamily: 'Vazir, sans-serif',
            direction: 'rtl',
            borderTop: '1px solid rgba(0,0,0,0.08)',
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select': {
              fontFamily: 'Vazir, sans-serif',
            },
            '& .MuiTablePagination-actions': {
              direction: 'ltr',
            },
          }}
        />
      </Card>

      {/* Add User Dialog */}
      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600 }}>
          افزودن پرسنل
        </DialogTitle>
        <Box component="form" onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              fullWidth
              label="نام"
              value={formData.firstName}
              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              required
              sx={{ mb: 2 }}
              InputLabelProps={{
                sx: { fontFamily: 'Vazir, sans-serif' },
              }}
              inputProps={{
                sx: { fontFamily: 'Vazir, sans-serif' },
              }}
            />
            <TextField
              fullWidth
              label="نام خانوادگی"
              value={formData.lastName}
              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              required
              sx={{ mb: 2 }}
              InputLabelProps={{
                sx: { fontFamily: 'Vazir, sans-serif' },
              }}
              inputProps={{
                sx: { fontFamily: 'Vazir, sans-serif' },
              }}
            />
            <TextField
              fullWidth
              label="شماره موبایل"
              type="tel"
              value={formData.mobile}
              onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
              required
              sx={{ mb: 2 }}
              autoComplete="off"
              InputLabelProps={{
                sx: { fontFamily: 'Vazir, sans-serif' },
              }}
              inputProps={{
                sx: { fontFamily: 'Vazir, sans-serif' },
                pattern: '09[0-9]{9}',
              }}
            />
            <TextField
              fullWidth
              label="کد پرسنلی"
              value={formData.personnelCode}
              onChange={(e) => setFormData({ ...formData, personnelCode: e.target.value })}
              required
              sx={{ mb: 2 }}
              InputLabelProps={{
                sx: { fontFamily: 'Vazir, sans-serif' },
              }}
              inputProps={{
                sx: { fontFamily: 'Vazir, sans-serif' },
              }}
            />
            <TextField
              select
              fullWidth
              label="گروه شغلی"
              value={formData.jobGroupId}
              onChange={(e) => setFormData({ ...formData, jobGroupId: e.target.value })}
              sx={{ mb: 2 }}
              InputLabelProps={{
                sx: { fontFamily: 'Vazir, sans-serif' },
              }}
              SelectProps={{
                native: true,
              }}
              inputProps={{
                sx: { fontFamily: 'Vazir, sans-serif' },
              }}
            >
              <option value="">انتخاب کنید</option>
              {jobGroups.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.title}
                </option>
              ))}
            </TextField>

            <TextField
              select
              fullWidth
              label="واحد سازمانی"
              value={formData.organizationalUnitId}
              onChange={(e) => setFormData({ ...formData, organizationalUnitId: e.target.value })}
              sx={{ mb: 2 }}
              InputLabelProps={{
                sx: { fontFamily: 'Vazir, sans-serif' },
              }}
              SelectProps={{
                native: true,
              }}
              inputProps={{
                sx: { fontFamily: 'Vazir, sans-serif' },
              }}
            >
              <option value="">انتخاب کنید</option>
              {organizationalUnits.map((unit) => (
                <option key={unit.id} value={unit.id}>
                  {unit.title}
                </option>
              ))}
            </TextField>
            <TextField
              select
              fullWidth
              label="جنسیت"
              value={formData.gender}
              onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
              sx={{ mb: 2 }}
              InputLabelProps={{
                sx: { fontFamily: 'Vazir, sans-serif' },
              }}
              SelectProps={{
                native: true,
              }}
              inputProps={{
                sx: { fontFamily: 'Vazir, sans-serif' },
              }}
            >
              <option value=""></option>
              <option value="male">مرد</option>
              <option value="female">زن</option>
            </TextField>
            <TextField
              select
              fullWidth
              label="شرکت"
              value={formData.companyId}
              onChange={(e) => setFormData({ ...formData, companyId: e.target.value })}
              sx={{ mb: 2 }}
              InputLabelProps={{
                sx: { fontFamily: 'Vazir, sans-serif' },
              }}
              SelectProps={{
                native: true,
              }}
              inputProps={{
                sx: { fontFamily: 'Vazir, sans-serif' },
              }}
            >
              <option value="">انتخاب کنید</option>
              {companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.title}
                </option>
              ))}
            </TextField>
            <TextField
              fullWidth
              label="شماره کارت"
              value={formData.cardNumber}
              onChange={(e) => setFormData({ ...formData, cardNumber: e.target.value })}
              sx={{ mb: 2 }}
              InputLabelProps={{
                sx: { fontFamily: 'Vazir, sans-serif' },
              }}
              inputProps={{
                sx: { fontFamily: 'Vazir, sans-serif' },
              }}
            />
            <TextField
              select
              fullWidth
              label="شیفت کاری"
              value={formData.workShift}
              onChange={(e) => setFormData({ ...formData, workShift: e.target.value as 'day' | 'night' })}
              required
              sx={{ mb: 2 }}
              InputLabelProps={{
                sx: { fontFamily: 'Vazir, sans-serif' },
              }}
              SelectProps={{
                native: true,
              }}
              inputProps={{
                sx: { fontFamily: 'Vazir, sans-serif' },
              }}
            >
              <option value="day">روز</option>
              <option value="night">شب</option>
            </TextField>
            <TextField
              fullWidth
              label="رمز عبور"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
              autoComplete="new-password"
              InputLabelProps={{
                sx: { fontFamily: 'Vazir, sans-serif' },
              }}
              inputProps={{
                sx: { fontFamily: 'Vazir, sans-serif' },
                minLength: 6,
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="نمایش رمز عبور"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={() => setOpenModal(false)}
              sx={{ fontFamily: 'Vazir, sans-serif' }}
            >
              انصراف
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={{ fontFamily: 'Vazir, sans-serif' }}
            >
              ذخیره
            </Button>
          </DialogActions>
        </Box>
      </Dialog >

      {/* Edit User Dialog */}
      < Dialog
        open={openEditModal}
        onClose={() => {
          setOpenEditModal(false);
          setEditingUser(null);
          setFormData({
            firstName: '',
            lastName: '',
            mobile: '',
            personnelCode: '',
            gender: '',
            company: '',
            companyId: '',
            cardNumber: '',
            workShift: 'day',
            password: '',
            jobGroupId: '',
            organizationalUnitId: '',
          });
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600 }}>
          ویرایش پرسنل
        </DialogTitle>
        <Box component="form" onSubmit={handleUpdate}>
          <DialogContent>
            <TextField
              fullWidth
              label="نام"
              value={formData.firstName}
              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              required
              sx={{ mb: 2 }}
              InputLabelProps={{
                sx: { fontFamily: 'Vazir, sans-serif' },
              }}
              inputProps={{
                sx: { fontFamily: 'Vazir, sans-serif' },
              }}
            />
            <TextField
              fullWidth
              label="نام خانوادگی"
              value={formData.lastName}
              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              required
              sx={{ mb: 2 }}
              InputLabelProps={{
                sx: { fontFamily: 'Vazir, sans-serif' },
              }}
              inputProps={{
                sx: { fontFamily: 'Vazir, sans-serif' },
              }}
            />
            <TextField
              fullWidth
              label="شماره موبایل"
              type="tel"
              value={formData.mobile}
              onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
              required
              sx={{ mb: 2 }}
              autoComplete="off"
              InputLabelProps={{
                sx: { fontFamily: 'Vazir, sans-serif' },
              }}
              inputProps={{
                sx: { fontFamily: 'Vazir, sans-serif' },
                pattern: '09[0-9]{9}',
              }}
            />
            <TextField
              fullWidth
              label="کد پرسنلی"
              value={formData.personnelCode}
              onChange={(e) => setFormData({ ...formData, personnelCode: e.target.value })}
              required
              sx={{ mb: 2 }}
              InputLabelProps={{
                sx: { fontFamily: 'Vazir, sans-serif' },
              }}
              inputProps={{
                sx: { fontFamily: 'Vazir, sans-serif' },
              }}
            />
            <TextField
              select
              fullWidth
              label="گروه شغلی"
              value={formData.jobGroupId}
              onChange={(e) => setFormData({ ...formData, jobGroupId: e.target.value })}
              sx={{ mb: 2 }}
              InputLabelProps={{
                sx: { fontFamily: 'Vazir, sans-serif' },
              }}
              SelectProps={{
                native: true,
              }}
              inputProps={{
                sx: { fontFamily: 'Vazir, sans-serif' },
              }}
            >
              <option value="">انتخاب کنید</option>
              {jobGroups.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.title}
                </option>
              ))}
            </TextField>
            <TextField
              select
              fullWidth
              label="واحد سازمانی"
              value={formData.organizationalUnitId}
              onChange={(e) => setFormData({ ...formData, organizationalUnitId: e.target.value })}
              sx={{ mb: 2 }}
              InputLabelProps={{
                sx: { fontFamily: 'Vazir, sans-serif' },
              }}
              SelectProps={{
                native: true,
              }}
              inputProps={{
                sx: { fontFamily: 'Vazir, sans-serif' },
              }}
            >
              <option value="">انتخاب کنید</option>
              {organizationalUnits.map((unit) => (
                <option key={unit.id} value={unit.id}>
                  {unit.title}
                </option>
              ))}
            </TextField>
            <TextField
              select
              fullWidth
              label="جنسیت"
              value={formData.gender}
              onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
              sx={{ mb: 2 }}
              InputLabelProps={{
                sx: { fontFamily: 'Vazir, sans-serif' },
              }}
              SelectProps={{
                native: true,
              }}
              inputProps={{
                sx: { fontFamily: 'Vazir, sans-serif' },
              }}
            >
              <option value=""></option>
              <option value="male">مرد</option>
              <option value="female">زن</option>
            </TextField>
            <TextField
              select
              fullWidth
              label="شرکت"
              value={formData.companyId}
              onChange={(e) => setFormData({ ...formData, companyId: e.target.value })}
              sx={{ mb: 2 }}
              InputLabelProps={{
                sx: { fontFamily: 'Vazir, sans-serif' },
              }}
              SelectProps={{
                native: true,
              }}
              inputProps={{
                sx: { fontFamily: 'Vazir, sans-serif' },
              }}
            >
              <option value="">انتخاب کنید</option>
              {companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.title}
                </option>
              ))}
            </TextField>
            <TextField
              fullWidth
              label="شماره کارت"
              value={formData.cardNumber}
              onChange={(e) => setFormData({ ...formData, cardNumber: e.target.value })}
              sx={{ mb: 2 }}
              InputLabelProps={{
                sx: { fontFamily: 'Vazir, sans-serif' },
              }}
              inputProps={{
                sx: { fontFamily: 'Vazir, sans-serif' },
              }}
            />
            <TextField
              select
              fullWidth
              label="شیفت کاری"
              value={formData.workShift}
              onChange={(e) => setFormData({ ...formData, workShift: e.target.value as 'day' | 'night' })}
              required
              sx={{ mb: 2 }}
              InputLabelProps={{
                sx: { fontFamily: 'Vazir, sans-serif' },
              }}
              SelectProps={{
                native: true,
              }}
              inputProps={{
                sx: { fontFamily: 'Vazir, sans-serif' },
              }}
            >
              <option value="day">روز</option>
              <option value="night">شب</option>
            </TextField>
            <TextField
              fullWidth
              label="رمز عبور (خالی بگذارید برای عدم تغییر)"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              autoComplete="new-password"
              InputLabelProps={{
                sx: { fontFamily: 'Vazir, sans-serif' },
              }}
              inputProps={{
                sx: { fontFamily: 'Vazir, sans-serif' },
                minLength: 6,
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="نمایش رمز عبور"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={() => {
                setOpenEditModal(false);
                setEditingUser(null);
                setFormData({
                  firstName: '',
                  lastName: '',
                  mobile: '',
                  personnelCode: '',
                  gender: '',
                  company: '',
                  companyId: '',
                  cardNumber: '',
                  workShift: 'day',
                  password: '',
                  jobGroupId: '',
                  organizationalUnitId: '',
                });
              }}
              sx={{ fontFamily: 'Vazir, sans-serif' }}
            >
              انصراف
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={{ fontFamily: 'Vazir, sans-serif' }}
            >
              ذخیره
            </Button>
          </DialogActions>
        </Box>
      </Dialog >
    </Box >
  );
};

export default AdminUsers;
